import createHttp from '@/config/http';
import type { IResponse, ResponseMetaData } from '@/types/service';

const http = createHttp(import.meta.env.VITE_NOTIFICATIONS_URL);

export interface NotificationItem {
  id?: string;
  notificationId: string;
  title: string;
  body: string;
  createdOn: string;
  isRead: boolean;
  by: string;
  data: Record<string, unknown>;
}

export interface UnReadCount { 
  count: number; 
}
export interface PendingNotificationItem {
  title: string;
  body: string;
  notificationId: string;
  clinicId: string;
}
export interface PendingNotification {
  userId: string;
  notifications: NotificationItem[];
}

// Notification Settings Types
export interface NotificationMethod {
  name: string;
  action: string;
  enabled: boolean;
}

export interface NotificationType {
  module: string;
  entityName: string;
  enabled: boolean;
  methods: NotificationMethod[];
}

export interface NotificationMedium {
  module: string;
  enabled: boolean;
}

export interface NotificationSettings {
  _id?: string;
  userId: string;
  types: NotificationType[];
  medium?: NotificationMedium[];
  createdAt?: string;
  updatedAt?: string;
}

export type UpdateNotificationsSettingsModuleBody = {
  entityName: string;
  enabled: boolean;
};

export type UpdateNotificationsSettingsMethodBody = {
  entityName: string;
  action: string;
  enabled: boolean;
};

export type UpdateBulkNotificationsSettingsBody = {
  types: NotificationType[];
};

export type NotificationSettingsResponse = IResponse<NotificationSettings>;

export const getNotificationsByUser = async (
  userId: string,
  page = 1,
  limit = 15,
  all: boolean = true,
  date: string = '',
): Promise<{
  items: NotificationItem[];
  meta: ResponseMetaData;
  totalUnreadItems: number;
}> => {
  const params: Record<string, string | number | boolean> = { page, limit, userId, all };
  
  if (date) {
    params.date = date;
  }

  const response = await http.get<{ data: { items: NotificationItem[]; metaData: ResponseMetaData; totalUnreadItems: number } }>(
    `/notifications/all`,
    {
      params,
    },
  );
  return {
    items: response.data.data.items,
    totalUnreadItems: response.data.data.totalUnreadItems,
    meta: response.data.data.metaData,
  };
};

export const getNotificationByUser = async (
  userId: string,
  notificationId: string,
): Promise<NotificationItem> => {
  const response = await http.get<{ data: NotificationItem }>(
    `/notifications/all/${userId}/${notificationId}`,
  );
  return response.data.data;
};

export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  await http.patch(`/notifications/all/read/${userId}`);
};

export const getPendingNotifications = async (
  userId: string,
  page = 1,
  limit = 5,
): Promise<PendingNotification> => {
  const response = await http.get<{ data: PendingNotification }>(
    `/notifications/pending/${userId}`,
    {
      params: { page, limit },
    },
  );
  return response.data.data;
};

export const removePendingNotifications = async (userId: string): Promise<void> => {
  await http.delete(`/notifications/pending/${userId}`);
};



export const getNotificationsSettings = async (userId: string): Promise<NotificationSettingsResponse> => {
  const response = await http.get<NotificationSettingsResponse>(
    `/notifications/settings/${userId}`,
  );
  return response.data;
};


export const updateNotificationsSettingsModule = async (
  userId: string,
  data: UpdateNotificationsSettingsModuleBody,
): Promise<NotificationSettingsResponse> => {
  const response = await http.patch<NotificationSettingsResponse>(
    `/notifications/settings/module/${userId}`,
    data
  );
  return response.data;
};

export const updateNotificationsSettingsMethod = async (
  userId: string,
  data: UpdateNotificationsSettingsMethodBody,
): Promise<NotificationSettingsResponse> => {
  const response = await http.patch<NotificationSettingsResponse>(
    `/notifications/settings/method/${userId}`,
    data
  );
  return response.data;
};

export const updateBulkNotificationsSettings = async (
  userId: string,
  data: UpdateBulkNotificationsSettingsBody,
): Promise<NotificationSettingsResponse> => {
  const response = await http.patch<NotificationSettingsResponse>(
    `/notifications/settings/bulk/${userId}`,
    data
  );
  return response.data;
};